import { Box, Button, LabeledInput } from '@factorialco/deprecated-design-system'
import {
  CreateCreditNoteSerialSequenceDocument,
  CreateSerialSequenceDocument,
  LegalEntityForSelectFragment,
  LegalEntitySelectorInitialValuesDocument,
} from 'generated/resources/graphql'

import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import { SingleRangeSlider } from 'modules/core/components/SingleRangeSlider'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SerialSequencesPreviewBox } from 'modules/finance/components/SerialSequences/PreviewBox'

export type SequenceType = 'invoice' | 'credit_note'

interface SerialSequenceModalFormValues {
  name: string
  prefix?: string
  suffix?: string
  legalEntity: LegalEntityForSelectFragment | null
  startNumber: number
  sequenceLength: number
  sequenceType: SequenceType
}

export const SerialSequenceModal = ({
  isOpen,
  onClose,
  refetchQuery,
  legalEntityId,
  name = '',
  sequenceType: fixedSequenceType,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  legalEntityId?: string
  name?: string
  /** If provided, the type is fixed and user cannot change it. If not provided, user can select. */
  sequenceType?: SequenceType
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data: legalEntityData } = useQuery(LegalEntitySelectorInitialValuesDocument, {
    variables: {
      companyId: companyId,
      ids: legalEntityId ? [legalEntityId] : [],
    },
    skip: !legalEntityId || !companyId,
  })

  const [createInvoiceSequence] = useMutation(CreateSerialSequenceDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceSerialSequenceSalesInvoiceSequence,
    refetchQueries: refetchQuery
      ? [refetchQuery, 'FinanceSalesInvoiceSequencesSelector']
      : ['FinanceSalesInvoiceSequencesSelector'],
  })

  const [createCreditNoteSequence] = useMutation(CreateCreditNoteSerialSequenceDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceSerialSequenceCreditNoteSequence,
    refetchQueries: refetchQuery
      ? [refetchQuery, 'FinanceCreditNoteSequencesSelector']
      : ['FinanceCreditNoteSequencesSelector'],
  })

  const showSequenceTypeSelector = fixedSequenceType === undefined

  const form = useForm<SerialSequenceModalFormValues>({
    defaultValues: {
      legalEntity: null,
      name: '',
      startNumber: 1,
      sequenceLength: 6,
      sequenceType: fixedSequenceType ?? 'invoice',
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<SerialSequenceModalFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (data.sequenceLength < 3) {
        errors.sequenceLength = i18n.t('finance.serial_sequences.modal.sequence_length_too_short')
      }

      if (data.sequenceLength > 8) {
        errors.sequenceLength = i18n.t('finance.serial_sequences.modal.sequence_length_too_long')
      }

      if (data.startNumber < 1) {
        errors.startNumber = i18n.t('finance.serial_sequences.modal.start_number_too_small')
      }

      if (!data.legalEntity) {
        errors.legalEntity = i18n.t('finance.serial_sequences.modal.legal_entity_required')
      }

      if (!data.sequenceType) {
        errors.sequenceType = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const formLegalEntityId = values.legalEntity?.id
      if (!formLegalEntityId) {
        return {
          errors: [],
          success: false,
        }
      }

      const variables = {
        legalEntityId: formLegalEntityId,
        name: values.name,
        prefix: values.prefix,
        suffix: values.suffix,
        sequenceLength: values.sequenceLength,
        startFrom: values.startNumber,
      }

      const handleErrors = (parsedErrors: Record<string, string | undefined>) => {
        if (
          parsedErrors.name ||
          parsedErrors.prefix ||
          parsedErrors.suffix ||
          parsedErrors.startFrom ||
          parsedErrors.sequenceLength ||
          parsedErrors.pattern
        ) {
          form.setErrors({
            name: parsedErrors.name,
            prefix: parsedErrors.prefix || parsedErrors.pattern,
            suffix: parsedErrors.suffix,
            startNumber: parsedErrors.startFrom,
            sequenceLength: parsedErrors.sequenceLength,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors || parsedErrors.base || i18n.t('errors.generic_error'),
          })
        }
      }

      const effectiveSequenceType = fixedSequenceType ?? values.sequenceType
      let success = false

      if (effectiveSequenceType === 'credit_note') {
        const result = await createCreditNoteSequence({ variables })
        const handled = handleResult(result, handleErrors)
        success = handled.success
      } else {
        const result = await createInvoiceSequence({ variables })
        const handled = handleResult(result, handleErrors)
        success = handled.success
      }

      if (success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.serial_sequences.modal.create_success'),
          time: 2000,
        })
        userClose()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  useUnsafeEffect(() => {
    if (legalEntityData?.companies.legalEntities.length) {
      form.setFieldValue('legalEntity', legalEntityData.companies.legalEntities[0])
    }
  }, [legalEntityData])

  useUnsafeEffect(() => {
    if (name) {
      form.setFieldValue('name', name)
    }
  }, [name])

  const userClose = () => {
    form.resetForm()
    onClose()
  }

  const serialPreview = useMemo(() => {
    const pattern = `${form.values.prefix ?? ''}{${Array(form.values.sequenceLength)
      .fill('X')
      .join('')}}${form.values.suffix ?? ''}`
    const startNumber = form.values.startNumber ?? 0

    return pattern.replace(
      /\{(X+)\}/,
      startNumber.toString().padStart(form.values.sequenceLength, 'X')
    )
  }, [form.values.prefix, form.values.suffix, form.values.sequenceLength, form.values.startNumber])

  if (!isOpen) return null

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('finance.serial_sequences.modal.new_sequence_title')}
      description={i18n.t('finance.serial_sequences.modal.new_sequence_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={userClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.save')}</Button>
        </>
      }
    >
      <Box gap="s16" overflowY="auto" style={{ maxHeight: '50vh' }}>
        <SerialSequencesPreviewBox preview={serialPreview} />
        {showSequenceTypeSelector && (
          <SelectField
            label={i18n.t('finance.serial_sequences.modal.sequence_type_label')}
            options={[
              {
                value: 'invoice',
                label: i18n.t('finance.serial_sequences.modal.sequence_type_invoice'),
              },
              {
                value: 'credit_note',
                label: i18n.t('finance.serial_sequences.modal.sequence_type_credit_note'),
              },
            ]}
            value={form.values.sequenceType}
            onChange={(value) => {
              if (value === 'invoice' || value === 'credit_note') {
                form.setFieldValue('sequenceType', value)
              }
            }}
            error={form.touched.sequenceType ? form.errors.sequenceType : undefined}
          />
        )}
        <LegalEntitySelector
          label={i18n.t('common.legal_entity')}
          emptyLabel={i18n.t('common.select_one')}
          value={form.values.legalEntity}
          onChange={(newValue) => form.setFieldValue('legalEntity', newValue)}
          error={form.touched.legalEntity ? form.errors.legalEntity : undefined}
          multiple={false}
          width="full"
        />
        <TextField
          label={i18n.t('finance.serial_sequences.modal.sequence_name_label')}
          value={form.values.name}
          onChange={(newValue) => form.setFieldValue('name', newValue)}
          error={form.touched.name ? form.errors.name : undefined}
        />
        <Box gap="s16" flexDirection="row">
          <TextField
            label={i18n.t('finance.serial_sequences.modal.prefix_label')}
            value={form.values.prefix}
            onChange={(newValue) => form.setFieldValue('prefix', newValue)}
            error={form.touched.prefix ? form.errors.prefix : undefined}
          />
          <TextField
            label={i18n.t('finance.serial_sequences.modal.suffix_label')}
            value={form.values.suffix}
            onChange={(newValue) => form.setFieldValue('suffix', newValue)}
            error={form.touched.suffix ? form.errors.suffix : undefined}
          />
        </Box>
        <NumberField
          label={i18n.t('finance.serial_sequences.modal.sequence_start_label')}
          value={form.values.startNumber}
          onChange={(newValue) => form.setFieldValue('startNumber', newValue)}
          error={form.touched.startNumber ? form.errors.startNumber : undefined}
          maxLength={form.values.sequenceLength}
        />
        <LabeledInput
          label={i18n.t('finance.serial_sequences.modal.sequence_length_label')}
          error={form.touched.sequenceLength ? form.errors.sequenceLength : undefined}
          selectValue=""
          missingLabel=""
          isLegend
        >
          <SingleRangeSlider
            min={3}
            max={8}
            value={form.values.sequenceLength}
            onChange={(newValue) => {
              form.setFieldValue('sequenceLength', newValue)
              form.setFieldValue(
                'startNumber',
                String(form.values.startNumber.toString().slice(0, newValue))
              )
            }}
          />
        </LabeledInput>
      </Box>
    </Modal>
  )
}
